import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Info } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { listingPostLabels } from "@/i18n";
import { useNativePlatform } from "@/hooks/useNativePlatform";

import { nativeNestedSheetDialogProps } from "@/lib/native/nativeNestedSheetProps";
import { cn } from "@/lib/utils";
import { cleanupNativeSheetChrome } from "@/lib/native/nativeSheetCleanup";

interface ImageSectionTipsProps {
  section: "exterior" | "interior" | "damage" | "service" | "tires";
  children?: React.ReactNode;
}

export const ImageSectionTips: React.FC<ImageSectionTipsProps> = ({
  section,
}) => {
  const { language } = useLanguage();
  const { isNative } = useNativePlatform();
  const [open, setOpen] = useState(false);
  const imagesLabels = listingPostLabels(language).images ?? {};
  const tips = (
    imagesLabels.tipsContent as Record<string, Record<string, unknown>>
  )?.[section];
  const tipsLabel = imagesLabels.tips ?? "Tips";
  const ariaLabel = (imagesLabels.tipsAria ?? "Tips för {section}").replace(
    "{section}",
    section,
  );

  const renderContent = () => {
    if (!tips) return null;
    const t = tips as Record<string, string | string[]>;

    if (section === "exterior") {
      const items = (t.items ?? []) as string[];
      const tipsItems = (t.tipsItems ?? []) as string[];
      return (
        <div className="text-sm space-y-3">
          <p>{t.intro}</p>
          <div>
            <p className="font-semibold mb-2">{t.recommendedTitle}</p>
            <ol className="list-decimal list-inside space-y-1 ml-2">
              {items.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ol>
          </div>
          <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
            <p className="font-semibold mb-1">{t.tipsTitle}</p>
            <ul className="list-disc list-inside space-y-1 text-xs">
              {tipsItems.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          </div>
        </div>
      );
    }
    if (section === "interior") {
      const items = (t.items ?? []) as string[];
      const tipsItems = (t.tipsItems ?? []) as string[];
      return (
        <div className="text-sm space-y-3">
          <p>{t.intro}</p>
          <div>
            <p className="font-semibold mb-2">{t.recommendedTitle}</p>
            <ol className="list-decimal list-inside space-y-1 ml-2">
              {items.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ol>
          </div>
          <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
            <p className="font-semibold mb-1">{t.tipsTitle}</p>
            <ul className="list-disc list-inside space-y-1 text-xs">
              {tipsItems.map((item, i) => (
                <li key={i}>✔ {item}</li>
              ))}
            </ul>
          </div>
        </div>
      );
    }
    if (section === "damage") {
      const sectionItems = (t.sectionItems ?? []) as string[];
      const dealerItems = (t.dealerItems ?? []) as string[];
      const dealerSublist = (t.dealerSublist ?? []) as string[];
      return (
        <div className="text-sm space-y-3">
          <div className="bg-yellow-50 dark:bg-yellow-900/20 p-3 rounded-lg">
            <p className="font-semibold mb-2">{t.sectionTitle}</p>
            <ul className="list-disc list-inside space-y-1 text-xs">
              {sectionItems.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          </div>
          <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
            <p className="font-semibold mb-1">{t.mottoTitle}</p>
            <p className="text-xs">👉 {t.mottoText}</p>
          </div>
          <div className="bg-gray-50 dark:bg-gray-900/20 p-3 rounded-lg">
            <p className="font-semibold mb-2">{t.dealerTitle}</p>
            <p className="text-xs mb-2">🔧 {t.dealerIntro}</p>
            <ul className="list-disc list-inside space-y-1 text-xs ml-2">
              {dealerItems.map((item, i) => (
                <li key={i}>
                  {item}
                  {i === 1 && (
                    <ul className="list-circle list-inside ml-4 mt-1">
                      {dealerSublist.map((sub, j) => (
                        <li key={j}>{sub}</li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
            <p className="text-xs mt-2">👉 {t.dealerOutro}</p>
          </div>
        </div>
      );
    }
    if (section === "service") {
      const items = (t.items ?? []) as string[];
      return (
        <div className="text-sm space-y-3">
          <p>{t.intro}</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            {items.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        </div>
      );
    }
    if (section === "tires") {
      const showItems = (t.showItems ?? []) as string[];
      return (
        <div className="text-sm space-y-3">
          <p>{t.intro}</p>
          <p>{t.perWheel}</p>
          <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
            <p className="font-semibold mb-1">{t.showTitle}</p>
            <ul className="list-disc list-inside space-y-1 text-xs">
              {showItems.map((item, i) => (
                <li key={i}>✔ {item}</li>
              ))}
            </ul>
          </div>
        </div>
      );
    }
    return null;
  };

  const nestedSheetProps = nativeNestedSheetDialogProps(open);

  return (
    <div className="flex items-center" onClick={(e) => e.stopPropagation()}>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) {
            requestAnimationFrame(() => cleanupNativeSheetChrome());
          }
        }}
      >
        <DialogTrigger asChild>
          <button
            type="button"
            className="flex items-center gap-1 px-2 py-1 rounded text-xs text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 focus:outline-none mr-[4px]"
            aria-label={ariaLabel}
          >
            <Info className="w-3 h-3" />
            {tipsLabel}
          </button>
        </DialogTrigger>
        <DialogContent
          {...nestedSheetProps}
          className={cn(
            nestedSheetProps.className,
            "max-w-lg max-h-[90vh] overflow-y-auto",
          )}
        >
          <DialogHeader>
            <DialogTitle>{tips?.title ?? section}</DialogTitle>
          </DialogHeader>
          {renderContent()}
        </DialogContent>
      </Dialog>
    </div>
  );
};
